import { useMutation, useQuery } from '@apollo/client';
import { InfoOutlined as IconInfoOutlined } from '@mui/icons-material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {
  DeletePersonMfa,
  GetMultifactorAuthStatus,
  MfaType,
  SwitchMfaActivation
} from 'Apollo';
import { PaperLoading } from 'Components/Shared';
import { PersonContext } from 'Context/CurrentPerson';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { TProfileManagerMessagesKeys } from 'Languages/TProfileManagerMessages';
import { useContext, useEffect, useRef } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';
import { PaperTitle } from 'Theme/components';
import joinURL from 'url-join';
import MfaUnavailableOrInitialize from './MfaUnavailableOrInitialize';

interface MfaDisplayProps {
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly isMfaActive: boolean;
  readonly type: MfaType;
}

interface MfaDeleteDisplayProps {
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly mfaType: MfaType;
}

interface MfaActionProps {
  readonly canUseMfa: boolean;
  readonly isInitialized: boolean;
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly isMfaActive: boolean;
  readonly type: MfaType;
}

function MfaDeleteDisplay({
  isCurrentUser,
  userImmutableId,
  mfaType
}: MfaDeleteDisplayProps) {
  const { currentPerson, currentPersonRoles } = useContext(PersonContext);
  const { onSuccess, onError } = useNotification();
  const history = useHistory();

  const onCompleted = () => {
    onSuccess('page.deletePersonMfa.success');
    history.goBack();
  };

  useMutation(DeletePersonMfa, {
    variables: {
      immutableId: userImmutableId,
      mfaType: mfaType
    },
    onError: error => {
      onError(error);
    },
    onCompleted
  });

  let redirectUri: string;

  redirectUri = generatePath(PrivateRoutes.deletePersonMfa.path, {
    accountCode: currentPerson.account ? currentPerson.account.code : 'all',
    immutableId: userImmutableId,
    targetedMfa: mfaType
  });

  if (isCurrentUser) {
    return (
      <IconButton
        data-testid="delete-totp-button"
        color="error"
        href={`${
          AppSettings.API_ENDPOINT_URI
        }api/mfa/challenge?redirectUri=${encodeURIComponent(
          joinURL('/', redirectUri)
        )}&targetOperation=${mfaType}`}
      >
        <DeleteForeverIcon />
      </IconButton>
    );
  }

  if (currentPersonRoles.canWriteAll) {
    return (
      <IconButton
        data-testid="delete-totp-button"
        color="error"
        href={redirectUri}
      >
        <DeleteForeverIcon />
      </IconButton>
    );
  }

  return null;
}

function MfaReadyDisplay({
  isCurrentUser,
  userImmutableId,
  isMfaActive,
  type
}: MfaDisplayProps) {
  const ts = useTranslate();

  let mfaReadyLabelKey = `page.userProfile.security.mfa.${type.toLowerCase()}.ready` as TProfileManagerMessagesKeys;
  return (
    <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      <Button color="success" disabled={true}>
        {isMfaActive
          ? ts(mfaReadyLabelKey)
          : ts('page.userProfile.security.mfa.mfaInactive')}
      </Button>
      <MfaDeleteDisplay
        isCurrentUser={isCurrentUser}
        userImmutableId={userImmutableId}
        mfaType={type}
      />
    </Box>
  );
}

function MfaAction({
  canUseMfa,
  isInitialized,
  isCurrentUser,
  userImmutableId,
  isMfaActive,
  type
}: MfaActionProps) {
  if (isInitialized) {
    //Cheking this condition first for an admin to know if it's user had configured TOTP
    return (
      <MfaReadyDisplay
        isCurrentUser={isCurrentUser}
        userImmutableId={userImmutableId}
        isMfaActive={isMfaActive}
        type={type}
      />
    );
  }

  let mfaInitializeLabelKey = `page.userProfile.security.mfa.${type.toLowerCase()}.initialize` as TProfileManagerMessagesKeys;
  return (
    <MfaUnavailableOrInitialize
      canUseMfa={canUseMfa}
      isCurrentUser={isCurrentUser}
      initializeLabel={mfaInitializeLabelKey}
      loginType={`initialize${type.toLowerCase()}mfa`}
    />
  );
}

interface CircleProps {
  readonly color?: string;
}

function Circle({ color }: CircleProps) {
  return (
    <Box
      sx={{
        bgcolor: color,
        width: 15,
        height: 15,
        marginRight: '-5px',
        marginLeft: '7px',
        borderRadius: '50%'
      }}
    />
  );
}

interface ActivationControlProps {
  readonly isMfaActive: boolean;
  readonly personImmutableId: string;
}

function ActivationControl({
  isMfaActive,
  personImmutableId
}: ActivationControlProps) {
  const ts = useTranslate();
  const theme = useTheme();
  const { currentPerson } = useContext(PersonContext);

  const handleToggle = () => {
    const redirectUri =
      generatePath(PrivateRoutes.usersProfile.path, {
        accountCode: currentPerson.account ? currentPerson.account.code : 'all',
        immutableId: personImmutableId
      }) + '/security/?switchMfa=true';

    const challengeUrl = `${
      AppSettings.API_ENDPOINT_URI
    }api/mfa/challenge?redirectUri=${encodeURIComponent(
      joinURL('/', redirectUri)
    )}`;
    globalThis.location.href = challengeUrl;
  };

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Chip
        label={
          isMfaActive
            ? ts('common.status.activated')
            : ts('common.status.disabled')
        }
        icon={
          <Circle
            color={
              isMfaActive ? theme.palette.primary.main : theme.palette.grey[500]
            }
          />
        }
      />
      <Button
        variant="contained"
        onClick={handleToggle}
        color={isMfaActive ? 'error' : 'primary'}
      >
        {isMfaActive
          ? ts('common.action.deactivate')
          : ts('common.action.activate')}
      </Button>
    </Box>
  );
}

interface Props {
  readonly person: IPersonWithAccount;
}

function TabSecurity({ person }: Props) {
  const ts = useTranslate();

  const { onError, onSuccess } = useNotification();
  const { currentPerson } = useContext(PersonContext);
  const { data, loading, refetch } = useQuery<GetMultifactorAuthStatusData>(
    GetMultifactorAuthStatus,
    {
      onError,
      variables: { immutableId: person.immutableId }
    }
  );

  const [switchPersonMfa] = useMutation<
    SwitchMfaActivationData,
    SwitchMfaActivationVariables
  >(SwitchMfaActivation, { onError });

  const executedRef = useRef(false);
  const isCurrentUser = currentPerson.immutableId === person.immutableId;

  const canUseMfa = !!data?.multifactorAuthStatus?.canUseMultifactor;
  const isTotpInitialized = !!data?.multifactorAuthStatus?.totpInitialized;
  const isEmailMfaInitialized = !!data?.multifactorAuthStatus
    ?.emailMfaInitialized;
  const isMfaActive = !!data?.multifactorAuthStatus?.isActive;

  const mfaStates = [
    {
      type: MfaType.Totp,
      isInitialized: isTotpInitialized,
      tooltip: ts('page.userProfile.security.mfa.totp.tooltip'),
      title: ts('page.userProfile.security.mfa.totp.title')
    },
    {
      type: MfaType.Email,
      isInitialized: isEmailMfaInitialized,
      tooltip: ts('page.userProfile.security.mfa.email.tooltip'),
      title: ts('page.userProfile.security.mfa.email.title')
    }
  ];

  const canToggleMfa =
    !loading && mfaStates.some(state => state.isInitialized) && isCurrentUser;

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const switchMfa = params.get('switchMfa');

    if (switchMfa === 'true' && !executedRef.current) {
      executedRef.current = true; // prevent double execution of the mutation

      (async () => {
        try {
          await switchPersonMfa({
            variables: { immutableId: person.immutableId }
          });
          const { data: refreshedData } = await refetch();
          const newStatus = refreshedData?.multifactorAuthStatus?.isActive;
          onSuccess(
            newStatus
              ? 'page.userProfile.security.mfa.activated'
              : 'page.userProfile.security.mfa.deactivated'
          );
        } finally {
          // Clean up URL
          params.delete('switchMfa');
          const newUrl = params.toString()
            ? `${globalThis.location.pathname}?${params.toString()}`
            : globalThis.location.pathname;
          globalThis.history.replaceState({}, '', newUrl);
        }
      })();
    }
  }, [
    person.immutableId,
    switchPersonMfa,
    refetch,
    onSuccess,
    ts,
    isMfaActive
  ]);

  return (
    <Paper>
      <PaperTitle
        title={ts('page.userProfile.security.mfa.title')}
        secondaryAction={
          canToggleMfa && (
            <ActivationControl
              isMfaActive={isMfaActive}
              personImmutableId={person.immutableId}
            />
          )
        }
      />
      <PaperLoading loading={loading}>
        <Grid container direction="column" rowSpacing={0}>
          {mfaStates.map(state => (
            <Grid item key={state.type}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                paddingX={2}
                paddingY={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">{state.title}</Typography>
                  <Tooltip title={state.tooltip}>
                    <IconInfoOutlined
                      fontSize="small"
                      sx={{ cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
                <MfaAction
                  canUseMfa={canUseMfa}
                  isInitialized={state.isInitialized}
                  isCurrentUser={isCurrentUser}
                  userImmutableId={person.immutableId}
                  isMfaActive={isMfaActive}
                  type={state.type}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </PaperLoading>
    </Paper>
  );
}

export default TabSecurity;
